import { Box, Stack, useTheme } from "@mui/material";
import { CompanyData_V2 } from "@rw1/auto-generated-clients";
import { FC } from "react";
import { ConfirmationDialog, LabeledTextField } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";
import { ValidationStatus } from "./ValidationStatus";
import { VerifyButton } from "./VerifyButton";

interface VerifyCompanyKeyDialogProps {
  isOpen: boolean;
  companyKey: string;
  setCompanyKey: (key: string) => void;
  handleVerify: () => void;
  handleCancel: () => void;
  verificationResults?: CompanyData_V2.Company[];
  isSubmitLoading?: boolean;
  isVerifyError: boolean;
  handleContinue?: () => void;
  isSameCompany?: boolean;
}

export const VerifyCompanyKeyDialog: FC<VerifyCompanyKeyDialogProps> = ({
  isOpen,
  companyKey,
  setCompanyKey,
  handleVerify,
  handleCancel,
  verificationResults,
  isSubmitLoading,
  isVerifyError,
  handleContinue,
  isSameCompany,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const isContinueDisabled =
    !verificationResults || verificationResults.length === 0 || isSameCompany;

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title showIcon={false}>
        {t("licenses.verifyCompanyKeyDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content>
        <Stack sx={{ gap: 3 }}>
          <Box>
            <LabeledTextField
              InputProps={{
                endAdornment: (
                  <VerifyButton
                    companyKey={companyKey}
                    isLoading={isSubmitLoading}
                    onClick={handleVerify}
                  />
                ),
              }}
              borderRadius={1}
              containerSx={{ height: 68 }}
              fullWidth
              height={40}
              label={t("licenses.verifyCompanyKeyDialog.companyKey")}
              maxLength={36}
              onChange={(e) => setCompanyKey(e.target.value)}
              paddingRight={1}
              placeholder={t(
                "licenses.verifyCompanyKeyDialog.companyKeyPlaceholder",
              )}
              value={companyKey}
            />
            <ValidationStatus
              companies={verificationResults}
              isError={isVerifyError}
              isSameCompany={isSameCompany}
            />
          </Box>
          <ConfirmationDialog.Text>
            {t("licenses.verifyCompanyKeyDialog.description")}
          </ConfirmationDialog.Text>
        </Stack>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{ color: theme.colors.primary[5] }}
          variant="secondary"
        >
          {t("licenses.verifyCompanyKeyDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isContinueDisabled}
          onClick={handleContinue}
          sx={{
            "&:disabled": {
              backgroundColor: theme.colors.primary[1],
              color: theme.colors.defaultBackground,
            },
          }}
          variant="contained"
        >
          {t("licenses.verifyCompanyKeyDialog.continue")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
